/**
 * @fileoverview Fresh user details query hook.
 * @description GET /users/{userId} for the dialogs that send a command against the details they
 * show (Edit, Deactivate, Reactivate). A lifecycle command marks every cached user stale without
 * refetching, so reopening a dialog could otherwise show the old ETag and `allowedActions` at once,
 * let the admin submit against them (a 412), and then remount the form when the background
 * refetch lands. This hook always refetches when the dialog opens and offers no details until that
 * read (or a later one) has landed, so the form is first filled from current data and a read after
 * that never replaces what the admin typed. Cached details can't be shown here, only the View
 * dialog's useUserDetail does that. Which details were already cached when the dialog opened is
 * remembered by their read time, so anything read after that counts as fresh.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { getUserDetail } from '@/features/users/services/users.service'

import type { VersionedUserDetail } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Reads one user's details afresh for a command dialog.
 * @param {string} userId - The user's id.
 * @returns {object} `detail` (undefined until a read made since the dialog opened has landed),
 * `isPending` (true until `detail` is there or the read failed), `error` and `refetch`.
 */
export function useFreshUserDetail(userId: string) {
  const queryClient = useQueryClient()
  // When the details already cached as the dialog opened were read (0 when there are none): only
  // a read made after that is current.
  const [cachedAt] = useState(
    () => queryClient.getQueryState(USERS_QUERY_KEYS.detail(userId))?.dataUpdatedAt ?? 0,
  )
  const query = useQuery<VersionedUserDetail, ApiError>({
    queryKey: USERS_QUERY_KEYS.detail(userId),
    queryFn: () => getUserDetail(userId),
    enabled: userId !== '',
    refetchOnMount: 'always',
  })
  const isFresh = query.data !== undefined && query.dataUpdatedAt > cachedAt

  return {
    detail: isFresh ? query.data : undefined,
    isPending: !isFresh && !query.isError,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}
